import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentType, CSSProperties, ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { cn } from "@/lib/utils";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { SupabaseAuthCard } from "@/components/supabase";
import { ensureStore, getMe } from "@/lib/karma/storage";
import { devSignOutLocal, signOut } from "@/lib/supabase/auth";
import { getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl, resilientFetch } from "@/lib/supabase/client";
import { MEMBERSHIP_PLANS } from "../../../shared/api";
import {
  fetchNotifications,
  getCurrentBrowserPushToken,
  registerNotificationDevice,
  sendProgressEvent,
} from "@/lib/progress";

import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

import {
  Circle,
  Compass,
  Heart,
  LayoutDashboard,
  Menu,
  MessageCircle,
  Salad,
  LogOut,
  Settings,
  ShoppingBag,
  ReceiptText,
  Sparkles,
  User,
  Users,
  Search,
  Wind,
  MapPin,
  Bell,
} from "lucide-react";

type PanelNavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  indent?: boolean;
};

type SearchSuggestion = {
  id: string;
  title: string;
  subtitle: string;
  to: string;
  group: "Sayfa" | "Ürün" | "Sipariş" | "Talep";
  searchText: string;
};

type SearchProduct = {
  id: string;
  name: string;
  sku: string;
  category: string;
  description: string;
  tags: string[];
};

type SearchOrder = {
  id: string;
  status: string;
  totalTry: number;
  packageKey: string;
};

type SearchRequest = {
  id: string;
  orderId: string;
  status: string;
  adSoyad: string;
  packageKey: string;
};

type MobilePlatform = "ios" | "android";

type MobileVerifyPayload = {
  platform: MobilePlatform;
  storeProductId: string;
  transactionId?: string;
  originalTransactionId?: string;
  purchaseToken?: string;
  receiptData?: string;
  receipt?: string;
  verificationPayload?: unknown;
  orderId?: string;
  requestId?: string;
};

function normalizeSearch(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z-\s/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

type PanelSidebarExtraContextValue = {
  extra: ReactNode;
  setExtra: (node: ReactNode) => void;
};

const PanelSidebarExtraContext = createContext<PanelSidebarExtraContextValue | null>(null);

export function usePanelSidebarExtra() {
  const ctx = useContext(PanelSidebarExtraContext);
  if (!ctx) throw new Error("usePanelSidebarExtra must be used within UserPanelLayout");
  return ctx;
}

const SOHBET_UNREAD_KEY = "karma_sohbet_unread_v1";
const NOTIFICATION_SEEN_AT_KEY = "panel_notifications_seen_at_v1";

const navItems: PanelNavItem[] = [
  { to: "/panel/akis", label: "Akış", icon: Sparkles },
  { to: "/panel/profil", label: "Sosyal Profil", icon: Users },
  { to: "/panel/bildirimler", label: "Bildirimler", icon: Bell },
  { to: "/panel/cember", label: "Çember", icon: Users },
  { to: "/panel/kesfet", label: "Keşfet", icon: Compass },
  { to: "/panel/kesfet/begeniler", label: "Beğeniler", icon: Heart, indent: true },
  { to: "/panel/kesfet/sohbet", label: "Sohbet", icon: MessageCircle, indent: true },
  { to: "/panel/yakinimdaki-ritueller", label: "Yakınımdaki Ritüeller", icon: MapPin },
];

const mobilePrimaryNavItems: PanelNavItem[] = [
  { to: "/panel/akis", label: "Akış", icon: Sparkles },
  { to: "/panel/kesfet", label: "Keşfet", icon: Compass },
  { to: "/panel/kesfet/sohbet", label: "Sohbet", icon: MessageCircle },
  { to: "/panel/bildirimler", label: "Bildirim", icon: Bell },
  { to: "/panel/profil", label: "Profil", icon: Users },
];

const PANEL_ROUTE_FEATURE_GATES = [
  {
    pathPrefix: "/panel/cember",
    featureKey: "exclusive_circles",
    description: "Özel çember ve etkinlik alanı Pro üyelikte açılır.",
    ctaFrom: "cember-upgrade",
  },
  {
    pathPrefix: "/panel/yakinimdaki-ritueller",
    featureKey: "nearby_rituals",
    description: "Yakınımdaki Ritüeller alanı Pro üyelikte açılır.",
    ctaFrom: "nearby-rituals-upgrade",
  },
] as const;

function getPanelHeaderMeta(pathname: string) {
  if (pathname.startsWith("/panel/akis")) return { title: "Akış", icon: Sparkles };
  if (pathname.startsWith("/panel/profil")) return { title: "Sosyal Profil", icon: Users };
  if (pathname.startsWith("/panel/bildirimler")) return { title: "Bildirimler", icon: Bell };
  if (pathname.startsWith("/panel/cember")) return { title: "Çember", icon: Circle };
  if (pathname.startsWith("/panel/kesfet/sohbet")) return { title: "Sohbet", icon: MessageCircle };
  if (pathname.startsWith("/panel/kesfet/begeniler")) return { title: "Beğeniler", icon: Heart };
  if (pathname.startsWith("/panel/kesfet")) return { title: "Keşfet", icon: Compass };
  if (pathname.startsWith("/panel/yakinimdaki-ritueller")) return { title: "Yakındaki Ritüeller", icon: MapPin };
  if (pathname.startsWith("/panel/magaza")) return { title: "Mağaza", icon: ShoppingBag };
  if (pathname.startsWith("/panel/siparislerim")) return { title: "Siparişlerim", icon: ReceiptText };
  if (pathname.startsWith("/panel/ayarlar")) return { title: "Ayarlar", icon: Settings };
  if (pathname.startsWith("/panel/destek")) return { title: "Destek Merkezi", icon: Settings };
  if (pathname.startsWith("/panel/meditasyon")) return { title: "Meditasyon", icon: Sparkles };
  if (pathname.startsWith("/panel/nefes")) return { title: "Nefes", icon: Wind };
  if (pathname.startsWith("/panel/diyet")) return { title: "Diyet", icon: Salad };
  if (pathname.startsWith("/panel/aktivite") || pathname.startsWith("/panel/rituel")) return { title: "Aktivite", icon: Sparkles };
  if (pathname.startsWith("/panel/gunluk")) return { title: "Günlük", icon: LayoutDashboard };
  return { title: "Panel", icon: Circle };
}

function isMobileNavItemActive(pathname: string, itemTo: string) {
  if (itemTo === "/panel/profil") return pathname === "/panel" || pathname.startsWith("/panel/profil");
  if (itemTo === "/panel/kesfet") return pathname.startsWith("/panel/kesfet");
  if (itemTo === "/panel/kesfet/sohbet") return pathname.startsWith("/panel/kesfet/sohbet");
  if (itemTo === "/panel/bildirimler") return pathname.startsWith("/panel/bildirimler");
  return pathname === itemTo;
}

export function UserPanelLayout() {
  const authDisabled = String((import.meta as any).env?.VITE_PANEL_AUTH_DISABLED ?? "").trim() === "1";

  const { toast } = useToast();
  const { user, loading } = useSupabaseSession();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [forceSignedOut, setForceSignedOut] = useState(false);
  const [sidebarExtra, setSidebarExtra] = useState<ReactNode>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchProducts, setSearchProducts] = useState<SearchProduct[]>([]);
  const [searchOrders, setSearchOrders] = useState<SearchOrder[]>([]);
  const [searchRequests, setSearchRequests] = useState<SearchRequest[]>([]);
  const [sohbetUnreadTotal, setSohbetUnreadTotal] = useState(0);
  const [notificationUnreadTotal, setNotificationUnreadTotal] = useState(0);
  const softDarkMode = true;
  const [recentlyLoggedIn, setRecentlyLoggedIn] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const dailyLoginEventSentRef = useRef("");
  const previousNotificationUnreadRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const effectiveUser = useMemo(() => {
    if (forceSignedOut && !authDisabled) return null;
    return user ?? (authDisabled ? ({ id: "guest", email: null, metadata: {} } as any) : null);
  }, [authDisabled, forceSignedOut, user]);

  const panelPrimary = useMemo(() => {
    const fromMeta = typeof effectiveUser?.metadata?.panel_primary === "string" ? (effectiveUser?.metadata?.panel_primary as string) : "";
    if (fromMeta) return fromMeta;

    try {
      const v = window.localStorage.getItem("panel_primary") ?? "";
      return v || "#7c3aed";
    } catch {
      return "#7c3aed";
    }
  }, [effectiveUser?.metadata?.panel_primary]);

  const onSignOut = async () => {
    setSigningOut(true);

    try {
      const raw = globalThis.localStorage?.getItem("dev_auth_user") ?? "";
      if (raw) {
        // Cancel all pending queries to prevent network errors
        queryClient.cancelQueries();
        devSignOutLocal();
        setRecentlyLoggedIn(false);
        setForceSignedOut(true);
        toast({ title: "Çıkış yapıldı", description: "Oturum kapatıldı." });
        setSigningOut(false);
        return;
      }
    } catch {
      // ignore
    }

    // Cancel all pending queries to prevent network errors during logout
    queryClient.cancelQueries();

    // Clear storage and mark as signed out immediately (don't wait for Supabase network call)
    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl));
    if (storageKey) {
      try {
        globalThis.localStorage?.removeItem(storageKey);
      } catch {
        // ignore
      }
    }

    setRecentlyLoggedIn(false);
    setForceSignedOut(true);
    toast({ title: "Çıkış yapıldı", description: "Oturum kapatıldı." });
    setSigningOut(false);

    // Keep local storage intact so user can access old messages on next login

    // Dispatch event so hooks know session changed
    globalThis.dispatchEvent?.(new Event("supabase-session-changed"));

    // Call Supabase signOut in background (don't wait for it)
    void (async () => {
      try {
        await signOut();
      } catch {
        // ignore
      }
    })();
  };

  const displayName = useMemo(() => {
    if (authDisabled) return "Misafir";

    const first = typeof effectiveUser?.metadata?.first_name === "string" ? (effectiveUser?.metadata?.first_name as string) : "";
    const last = typeof effectiveUser?.metadata?.last_name === "string" ? (effectiveUser?.metadata?.last_name as string) : "";
    const full = `${first} ${last}`.trim();

    if (full) return full;

    const email = effectiveUser?.email ?? "";
    return email ? email.split("@")[0] : "Kullanıcı";
  }, [authDisabled, effectiveUser?.email, effectiveUser?.metadata?.first_name, effectiveUser?.metadata?.last_name]);

  const panelHeader = useMemo(() => getPanelHeaderMeta(location.pathname), [location.pathname]);
  const isSohbetDetailRoute = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const matchId = String(params.get("match") ?? params.get("matchId") ?? "").trim();
    const peerId = String(params.get("peer") ?? params.get("peerId") ?? "").trim();
    const hasDetailQuery = Boolean(matchId && peerId);
    const isKesfetSohbetPath = location.pathname.startsWith("/panel/kesfet/sohbet") || location.pathname === "/panel/kesfet";
    return hasDetailQuery && isKesfetSohbetPath;
  }, [location.pathname, location.search]);
  // Check user?.id or if recently logged in (storing session details takes time)
  const isAuthed = authDisabled ? true : (!forceSignedOut && (Boolean(user?.id) || recentlyLoggedIn));

  const playNotificationSound = () => {
    if (typeof window === "undefined") return;

    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = audioContextRef.current ?? new AudioCtx();
      audioContextRef.current = ctx;

      if (ctx.state === "suspended") {
        void ctx.resume().catch(() => undefined);
      }

      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 880;
      gainNode.gain.value = 0.0001;
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      const now = ctx.currentTime;
      gainNode.gain.exponentialRampToValueAtTime(0.08, now + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
      oscillator.start(now);
      oscillator.stop(now + 0.24);
    } catch {
      // ignore audio errors
    }
  };

  useEffect(() => {
    const readUnreadTotal = () => {
      try {
        const raw = globalThis.localStorage?.getItem(SOHBET_UNREAD_KEY) ?? "{}";
        const parsed = JSON.parse(raw) as Record<string, number>;
        const total = Object.values(parsed).reduce((sum, value) => sum + (Number(value) || 0), 0);
        setSohbetUnreadTotal(total);
      } catch {
        setSohbetUnreadTotal(0);
      }
    };

    readUnreadTotal();

    const onStorage = (event: StorageEvent) => {
      if (!event.key || event.key === SOHBET_UNREAD_KEY) readUnreadTotal();
    };

    const onUnreadChanged = () => readUnreadTotal();

    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener("karma-sohbet-unread-changed", onUnreadChanged as EventListener);

    return () => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener("karma-sohbet-unread-changed", onUnreadChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const payload = event.data as { type?: string; deepLink?: string } | null;
      if (!payload) return;

      if (payload.type === "notification-click") {
        const deepLink =
          typeof payload.deepLink === "string" && payload.deepLink.trim() ? payload.deepLink.trim() : "/panel/bildirimler";
        navigate(deepLink);
        return;
      }

      if (payload.type === "push-subscription-change") {
        void (async () => {
          const token = await getCurrentBrowserPushToken();
          if (!token?.endpoint) return;

          await registerNotificationDevice({
            platform: "web",
            push_provider: "webpush",
            device_token: token.endpoint,
            endpoint: token.endpoint,
            p256dh: token.p256dh,
            auth_secret: token.authSecret,
            locale: typeof navigator.language === "string" ? navigator.language : undefined,
          }).catch(() => null);
        })();
        return;
      }

      if (payload.type === "notification-arrived") {
        playNotificationSound();
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [navigate]);

  useEffect(() => {
    setMobileNavOpen(false);
    setSidebarExtra(null);
    setSearchOpen(false);
    setActiveSearchIndex(0);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthed) {
      previousNotificationUnreadRef.current = null;
      return;
    }

    if (previousNotificationUnreadRef.current == null) {
      previousNotificationUnreadRef.current = notificationUnreadTotal;
      return;
    }

    if (notificationUnreadTotal > previousNotificationUnreadRef.current) {
      playNotificationSound();
    }

    previousNotificationUnreadRef.current = notificationUnreadTotal;
  }, [isAuthed, notificationUnreadTotal]);

  useEffect(() => {
    if (!isAuthed) {
      setNotificationUnreadTotal(0);
      return;
    }

    const readSeenAt = () => {
      try {
        const raw = globalThis.localStorage?.getItem(NOTIFICATION_SEEN_AT_KEY) ?? "";
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
      } catch {
        return 0;
      }
    };

    const writeSeenAtNow = () => {
      try {
        globalThis.localStorage?.setItem(NOTIFICATION_SEEN_AT_KEY, String(Date.now()));
      } catch {
        // ignore
      }
    };

    if (location.pathname.startsWith("/panel/bildirimler")) {
      writeSeenAtNow();
      setNotificationUnreadTotal(0);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const loadUnread = async () => {
      try {
        const payload = await fetchNotifications(100);
        if (cancelled) return;

        const seenAtMs = readSeenAt();
        const unseenCount = (payload.items ?? []).filter((item) => {
          if (item.status !== "unread") return false;
          const createdAtMs = new Date(String(item.created_at ?? "")).getTime();
          if (!Number.isFinite(createdAtMs)) return true;
          return createdAtMs > seenAtMs;
        }).length;

        setNotificationUnreadTotal(Math.max(0, unseenCount));
      } catch {
        if (!cancelled) setNotificationUnreadTotal(0);
      }
    };

    void loadUnread();
    timer = globalThis.setInterval(() => {
      void loadUnread();
    }, 8000);

    return () => {
      cancelled = true;
      if (timer) globalThis.clearInterval(timer);
    };
  }, [isAuthed, location.pathname]);

  useEffect(() => {
    if (authDisabled || !isAuthed || !user?.id) return;

    const dateKey = new Date().toISOString().slice(0, 10);
    const marker = `${user.id}:${dateKey}`;
    if (dailyLoginEventSentRef.current === marker) return;

    dailyLoginEventSentRef.current = marker;
    void sendProgressEvent({ eventType: "daily_login", dateKey }).catch(() => undefined);
  }, [authDisabled, isAuthed, user?.id]);

  const readStoredAccessToken = () => {
    try {
      const parseToken = (raw: string) => {
        if (!raw) return "";
        const direct = raw.trim();
        if (direct && !direct.startsWith("{")) return direct;

        const parsed = raw ? (JSON.parse(raw) as any) : null;
        const fromRoot = typeof parsed?.access_token === "string" ? parsed.access_token : "";
        if (fromRoot) return fromRoot;
        const fromCurrentSession = typeof parsed?.currentSession?.access_token === "string" ? parsed.currentSession.access_token : "";
        if (fromCurrentSession) return fromCurrentSession;
        const fromSession = typeof parsed?.session?.access_token === "string" ? parsed.session.access_token : "";
        return fromSession || "";
      };

      const { directUrl } = getSupabaseConfig();
      const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
      const fromSupabaseStorage = storageKey ? parseToken(globalThis.localStorage?.getItem(storageKey) ?? "") : "";
      if (fromSupabaseStorage) return fromSupabaseStorage;

      const fromLegacyKey = parseToken(globalThis.localStorage?.getItem("supabase.auth.token") ?? "");
      if (fromLegacyKey) return fromLegacyKey;

      return "";
    } catch {
      return "";
    }
  };

  useEffect(() => {
    if (!isAuthed) return;

    let cancelled = false;

    const parseJsonResponse = async (res: Response) => {
      return (await res.json().catch(() => null)) as any;
    };

    const verifyMobilePurchase = async (payload: MobileVerifyPayload, opts?: { silent?: boolean }) => {
      const token = readStoredAccessToken();
      if (!token) throw new Error("Oturum bulunamadı.");

      const platform = payload.platform;
      if (platform !== "ios" && platform !== "android") {
        throw new Error("platform yalnızca ios veya android olabilir.");
      }

      const body = {
        platform,
        storeProductId: String(payload.storeProductId ?? "").trim(),
        transactionId: String(payload.transactionId ?? "").trim() || undefined,
        originalTransactionId: String(payload.originalTransactionId ?? "").trim() || undefined,
        purchaseToken: String(payload.purchaseToken ?? "").trim() || undefined,
        receiptData: String(payload.receiptData ?? payload.receipt ?? "").trim() || undefined,
        orderId: String(payload.orderId ?? "").trim() || undefined,
        verificationPayload: payload.verificationPayload ?? null,
      };

      const res = await resilientFetch("/api/numeroloji/payments/mobile/verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const json = await parseJsonResponse(res);
      if (!res.ok || !json?.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Mobil satın alma doğrulanamadı.");
      }

      if (!opts?.silent) {
        toast({ title: "Satın alma doğrulandı", description: "Mobil satın alma hesabına tanımlandı." });
      }

      globalThis.dispatchEvent?.(
        new CustomEvent("numeroloji:mobile-payment-verified", {
          detail: {
            requestId: String(payload.requestId ?? "").trim() || null,
            result: json,
          },
        }),
      );

      return json;
    };

    const restoreMobilePurchases = async (platform?: MobilePlatform, opts?: { silent?: boolean; requestId?: string }) => {
      const token = readStoredAccessToken();
      if (!token) throw new Error("Oturum bulunamadı.");

      const body = platform ? { platform } : {};
      const res = await resilientFetch("/api/numeroloji/payments/mobile/restore", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const json = await parseJsonResponse(res);
      if (!res.ok || !json?.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Mobil satın alımlar geri yüklenemedi.");
      }

      if (!opts?.silent) {
        const restoredCount = Array.isArray(json?.items) ? json.items.length : 0;
        toast({
          title: "Satın alımlar geri yüklendi",
          description: restoredCount > 0 ? `${restoredCount} kayıt bulundu.` : "Aktif mobil satın alma bulunamadı.",
        });
      }

      globalThis.dispatchEvent?.(
        new CustomEvent("numeroloji:mobile-payment-restore", {
          detail: {
            requestId: String(opts?.requestId ?? "").trim() || null,
            result: json,
          },
        }),
      );

      return json;
    };

    const bridge = {
      verifyPurchase: (payload: MobileVerifyPayload) => verifyMobilePurchase(payload),
      restorePurchases: (platform?: MobilePlatform) => restoreMobilePurchases(platform),
    };

    (globalThis as any).NumerolojiMobilePayments = bridge;

    const queueKey = "__numerolojiMobileBridgeQueue";
    const pendingQueue = Array.isArray((globalThis as any)[queueKey])
      ? (((globalThis as any)[queueKey] as Array<any>).splice(0) as Array<any>)
      : [];

    for (const item of pendingQueue) {
      if (cancelled || !item || typeof item !== "object") continue;
      const type = String((item as any).type ?? "").trim();
      if (type === "verify") {
        const detail = (item as any).payload as MobileVerifyPayload;
        if (!detail) continue;
        void verifyMobilePurchase(detail).catch((error) => {
          const message = error instanceof Error ? error.message : "Mobil satın alma doğrulanamadı.";
          globalThis.dispatchEvent?.(
            new CustomEvent("numeroloji:mobile-payment-error", {
              detail: {
                requestId: String(detail.requestId ?? "").trim() || null,
                message,
              },
            }),
          );
        });
      } else if (type === "restore") {
        const detail = ((item as any).payload ?? {}) as { platform?: MobilePlatform; requestId?: string };
        void restoreMobilePurchases(detail.platform, { silent: true, requestId: detail.requestId }).catch((error) => {
          const message = error instanceof Error ? error.message : "Mobil satın alımlar geri yüklenemedi.";
          globalThis.dispatchEvent?.(
            new CustomEvent("numeroloji:mobile-payment-error", {
              detail: {
                requestId: String(detail.requestId ?? "").trim() || null,
                message,
              },
            }),
          );
        });
      }
    }

    const onVerifyRequested = (event: Event) => {
      const detail = (event as CustomEvent<MobileVerifyPayload>)?.detail;
      if (!detail || cancelled) return;
      void verifyMobilePurchase(detail).catch((error) => {
        const message = error instanceof Error ? error.message : "Mobil satın alma doğrulanamadı.";
        toast({ title: "Doğrulama hatası", description: message });
        globalThis.dispatchEvent?.(
          new CustomEvent("numeroloji:mobile-payment-error", {
            detail: {
              requestId: String(detail.requestId ?? "").trim() || null,
              message,
            },
          }),
        );
      });
    };

    const onRestoreRequested = (event: Event) => {
      const detail = (event as CustomEvent<{ platform?: MobilePlatform; requestId?: string }>)?.detail;
      if (cancelled) return;
      void restoreMobilePurchases(detail?.platform, { requestId: detail?.requestId }).catch((error) => {
        const message = error instanceof Error ? error.message : "Mobil satın alımlar geri yüklenemedi.";
        toast({ title: "Geri yükleme hatası", description: message });
        globalThis.dispatchEvent?.(
          new CustomEvent("numeroloji:mobile-payment-error", {
            detail: {
              requestId: String(detail?.requestId ?? "").trim() || null,
              message,
            },
          }),
        );
      });
    };

    globalThis.addEventListener?.("numeroloji:mobile-payment-verify-request", onVerifyRequested as EventListener);
    globalThis.addEventListener?.("numeroloji:mobile-payment-restore-request", onRestoreRequested as EventListener);

    void restoreMobilePurchases(undefined, { silent: true }).catch(() => undefined);

    return () => {
      cancelled = true;
      if ((globalThis as any).NumerolojiMobilePayments === bridge) {
        delete (globalThis as any).NumerolojiMobilePayments;
      }
      globalThis.removeEventListener?.("numeroloji:mobile-payment-verify-request", onVerifyRequested as EventListener);
      globalThis.removeEventListener?.("numeroloji:mobile-payment-restore-request", onRestoreRequested as EventListener);
    };
  }, [isAuthed, toast]);

  useEffect(() => {
    if (!isAuthed) return;

    let cancelled = false;

    const safeFetchJson = async (url: string, init?: RequestInit) => {
      try {
        const res = await resilientFetch(url, init);
        if (!res.ok) return null;
        return (await res.json().catch(() => null)) as any;
      } catch {
        return null;
      }
    };

    const loadSearchData = async () => {
      setSearchLoading(true);

      try {
        const token = readStoredAccessToken();
        const [catalogJson, panelJson] = await Promise.all([
          safeFetchJson("/api/numeroloji/catalog"),
          token
            ? safeFetchJson("/api/numeroloji/panel/me", {
                headers: { Authorization: `Bearer ${token}` },
              })
            : Promise.resolve(null),
        ]);

        if (!cancelled && catalogJson?.ok && Array.isArray(catalogJson?.products)) {
          const products = catalogJson.products
            .map((row: any) => ({
              id: String(row?.id ?? "").trim(),
              name: String(row?.name ?? "").trim(),
              sku: String(row?.sku ?? "").trim(),
              category: String(row?.category ?? "").trim(),
              description: String(row?.description ?? "").trim(),
              tags: Array.isArray(row?.tags) ? row.tags.map((tag: unknown) => String(tag ?? "").trim()).filter(Boolean) : [],
            }))
            .filter((row: SearchProduct) => row.id && row.name);
          setSearchProducts(products);
        }

        if (!cancelled && panelJson?.ok) {
          const nextOrders = Array.isArray(panelJson?.orders)
            ? panelJson.orders
                .map((row: any) => ({
                  id: String(row?.id ?? "").trim(),
                  status: String(row?.status ?? "").trim(),
                  totalTry: Number(row?.totalTry ?? 0) || 0,
                  packageKey: String(row?.packageKey ?? "").trim(),
                }))
                .filter((row: SearchOrder) => row.id)
            : [];

          const nextRequests = Array.isArray(panelJson?.requests)
            ? panelJson.requests
                .map((row: any) => ({
                  id: String(row?.id ?? "").trim(),
                  orderId: String(row?.orderId ?? "").trim(),
                  status: String(row?.status ?? "").trim(),
                  adSoyad: String(row?.adSoyad ?? "").trim(),
                  packageKey: String(row?.packageKey ?? "").trim(),
                }))
                .filter((row: SearchRequest) => row.id)
            : [];

          setSearchOrders(nextOrders);
          setSearchRequests(nextRequests);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    };

    void loadSearchData();

    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  const readStoredUserId = () => {
    try {
      const { directUrl } = getSupabaseConfig();
      const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
      if (!storageKey) return "";
      const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
      const parsed = raw ? (JSON.parse(raw) as any) : null;
      return String(parsed?.user?.id ?? "");
    } catch {
      return "";
    }
  };

  useEffect(() => {
    // If user exists, they're logged in
    if (user?.id) {
      setForceSignedOut(false);
      return;
    }

    // If user is null and loading is done, they've logged out
    if (user === null && !loading) {
      setForceSignedOut(true);
    }
  }, [user?.id, loading]);

  useEffect(() => {
    if (authDisabled) return;

    const sync = () => {
      const storedUserId = readStoredUserId();
      // Storage is updated synchronously (localStorage), so we can rely on it directly.
      // If storage has a session, clear the forced signed-out state.
      // Never force-logout via sync - logout happens via explicit setForceSignedOut(true) call.
      if (storedUserId) {
        setForceSignedOut(false);
      }
    };

    globalThis.addEventListener?.("supabase-session-changed", sync);
    // Run once on mount.
    sync();

    return () => {
      globalThis.removeEventListener?.("supabase-session-changed", sync);
    };
  }, [authDisabled]);

  const membershipSummaryQuery = useQuery({
    queryKey: ["numeroloji-membership-summary", user?.id],
    enabled: isAuthed && Boolean(user?.id),
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const token = readStoredAccessToken();
      if (!token) return null;

      try {
        const res = await resilientFetch("/api/numeroloji/me/entitlements", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) return null;
        const payload = (await res.json().catch(() => null)) as any;
        return payload?.membership ?? null;
      } catch (error) {
        if (error instanceof TypeError && String(error).includes("Failed to fetch")) {
          return null;
        }
        return null;
      }
    },
  });

  const membershipHasFeature = useMemo(() => {
    if (authDisabled) return {} as Record<string, boolean>;
    const raw = membershipSummaryQuery.data?.hasFeature;
    if (!raw || typeof raw !== "object") return {} as Record<string, boolean>;
    return raw as Record<string, boolean>;
  }, [authDisabled, membershipSummaryQuery.data?.hasFeature]);

  const activeRouteFeatureGate = useMemo(() => {
    return PANEL_ROUTE_FEATURE_GATES.find((gate) => location.pathname.startsWith(gate.pathPrefix)) ?? null;
  }, [location.pathname]);

  const hasResolvedMembershipSnapshot = Boolean(membershipSummaryQuery.data && typeof membershipSummaryQuery.data === "object");

  const shouldLockCurrentRoute =
    !authDisabled &&
    isAuthed &&
    Boolean(user?.id) &&
    !membershipSummaryQuery.isLoading &&
    hasResolvedMembershipSnapshot &&
    Boolean(activeRouteFeatureGate) &&
    !Boolean(membershipHasFeature[activeRouteFeatureGate.featureKey]);

  const baseSearchSuggestions = useMemo<SearchSuggestion[]>(() => {
    const pageItems = navItems.map((item) => ({
      id: `page:${item.to}`,
      title: item.label,
      subtitle: "Panel sayfası",
      to: item.to,
      group: "Sayfa" as const,
      searchText: normalizeSearch(`${item.label} ${item.to.replace(/\//g, " ")}`),
    }));

    return [
      ...pageItems,
      {
        id: "page:/panel/profil",
        title: "Sosyal Profil",
        subtitle: "Topluluk akışı ve öneriler",
        to: "/panel/profil",
        group: "Sayfa",
        searchText: normalizeSearch("sosyal profil topluluk akışı öneriler panel"),
      },
      {
        id: "page:/panel/ayarlar",
        title: "Kullanıcı Ayarları",
        subtitle: "Kişisel bilgiler ve güvenlik",
        to: "/panel/ayarlar",
        group: "Sayfa",
        searchText: normalizeSearch("kullanıcı ayarları kişisel bilgiler güvenlik adresler numeroloji panel"),
      },
      {
        id: "page:/panel/destek",
        title: "Destek Merkezi",
        subtitle: "Yardım ve destek",
        to: "/panel/destek",
        group: "Sayfa",
        searchText: normalizeSearch("destek merkezi yardım panel"),
      },
    ];
  }, []);

  const allSearchSuggestions = useMemo<SearchSuggestion[]>(() => {
    const productSuggestions = searchProducts.map((product) => {
      const subtitle = product.sku ? `${product.category || "Mağaza"} • SKU: ${product.sku}` : product.category || "Mağaza ürünü";
      return {
        id: `product:${product.id}`,
        title: product.name,
        subtitle,
        to: `/panel/magaza/urun/${encodeURIComponent(product.id)}`,
        group: "Ürün" as const,
        searchText: normalizeSearch(`${product.name} ${product.sku} ${product.category} ${product.description} ${product.tags.join(" ")}`),
      };
    });

    const orderSuggestions = searchOrders.map((order) => ({
      id: `order:${order.id}`,
      title: `Sipariş #${order.id}`,
      subtitle: `${order.packageKey || "Paket"} • ${order.status || "durum yok"} • ${Math.round(order.totalTry)} ₺`,
      to: "/panel/siparislerim",
      group: "Sipariş" as const,
      searchText: normalizeSearch(`${order.id} ${order.status} ${order.packageKey}`),
    }));

    const requestSuggestions = searchRequests.map((request) => ({
      id: `request:${request.id}`,
      title: `Talep #${request.id}`,
      subtitle: `${request.adSoyad || "İsim yok"} • ${request.status || "durum yok"}`,
      to: "/panel/siparislerim",
      group: "Talep" as const,
      searchText: normalizeSearch(`${request.id} ${request.orderId} ${request.status} ${request.adSoyad} ${request.packageKey}`),
    }));

    return [...baseSearchSuggestions, ...productSuggestions, ...orderSuggestions, ...requestSuggestions];
  }, [baseSearchSuggestions, searchOrders, searchProducts, searchRequests]);

  const filteredSearchSuggestions = useMemo(() => {
    const q = normalizeSearch(searchQuery);

    if (!q) {
      return allSearchSuggestions.filter((item) => item.group === "Sayfa").slice(0, 8);
    }

    return allSearchSuggestions
      .map((item) => {
        let score = 0;
        if (item.searchText === q) score += 300;
        if (normalizeSearch(item.title).startsWith(q)) score += 180;
        if (item.searchText.includes(` ${q}`)) score += 120;
        if (item.searchText.includes(q)) score += 80;
        if (item.group === "Sayfa") score += 5;
        return { item, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map((entry) => entry.item);
  }, [allSearchSuggestions, searchQuery]);


  useEffect(() => {
    setActiveSearchIndex(0);
  }, [searchQuery, searchOpen]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (!searchContainerRef.current) return;
      if (searchContainerRef.current.contains(event.target as Node)) return;
      setSearchOpen(false);
      setActiveSearchIndex(0);
    };

    globalThis.addEventListener?.("mousedown", onMouseDown);
    return () => {
      globalThis.removeEventListener?.("mousedown", onMouseDown);
    };
  }, []);

  const openSuggestion = (item: SearchSuggestion) => {
    setSearchQuery("");
    setSearchOpen(false);
    setActiveSearchIndex(0);
    navigate(item.to);
  };

  const isPublicRoute = location.pathname.startsWith("/panel/destek");

  if (!isPublicRoute && !authDisabled && loading && !user?.id) {
    return <div className="p-6 text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  // During loading, if user just logged in (forceSignedOut=false), keep showing the panel
  // Otherwise show loading state
  if (!isPublicRoute && !authDisabled && loading && forceSignedOut) {
    return <div className="p-6 text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  // Show auth card if:
  // - User doesn't exist AND not recently logged in
  // - OR authentication is required and user not authed
  if (!isPublicRoute && ((!effectiveUser && !recentlyLoggedIn) || (!authDisabled && !isAuthed))) {
    const store = ensureStore();
    const me = getMe(store);

    const subtitle = "Kayıt ol ve panelini kullanmaya başla";

    return (
      <div
        className="relative flex min-h-dvh w-full items-center justify-center overflow-hidden bg-[#131022] px-4 py-10 text-slate-100"
        style={{ "--panel-primary": panelPrimary } as CSSProperties}
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(55,19,236,0.30)_0%,_transparent_55%)]" />
        <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:26px_26px]" />

        <div className="relative w-full max-w-lg">
          <div className="mb-5 flex items-center gap-3">
            <div className="grid size-9 place-items-center rounded-xl bg-[var(--panel-primary)] text-white shadow-lg shadow-[color:rgba(55,19,236,0.35)]">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-extrabold tracking-tight">Numeroloji Asistanı</div>
              <div className="text-xs text-slate-300">{subtitle}</div>
            </div>
          </div>

          <SupabaseAuthCard
            me={me}
            onLoginSuccess={() => {
              setForceSignedOut(false);
              // Mark that user just logged in so we don't show auth card during data loading
              setRecentlyLoggedIn(true);
              const timer = setTimeout(() => {
                setRecentlyLoggedIn(false);
              }, 20000);
              // Cleanup if component unmounts
              return () => clearTimeout(timer);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <PanelSidebarExtraContext.Provider value={{ extra: sidebarExtra, setExtra: setSidebarExtra }}>
      <div
        className={cn(
          "flex min-h-dvh w-full max-w-full overflow-hidden",
          softDarkMode
            ? "bg-[#f4edf8] text-slate-900"
            : "dark bg-[#130d24] text-slate-100 dark:bg-[#130d24] dark:text-slate-100",
          softDarkMode && "panel-soft",
        )}
        style={{ "--panel-primary": panelPrimary } as CSSProperties}
      >
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="bottom"
          className={cn(
            "rounded-t-[28px] border-x-0 border-b-0 px-5 pb-8 pt-6 backdrop-blur-sm",
            softDarkMode
              ? "bg-[#efe7f2] text-slate-900 shadow-[0_-20px_50px_rgba(124,58,237,0.18)]"
              : "bg-[#130d24] text-slate-100 shadow-[0_-24px_60px_rgba(0,0,0,0.45)]",
          )}
        >
          <SheetHeader>
            <SheetTitle className="sr-only">Panel menüsü</SheetTitle>
          </SheetHeader>
          <div className="flex h-full min-h-0 flex-col justify-between">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex flex-col gap-8">
                <div className="flex items-center gap-3">
                <div className="grid size-12 place-items-center overflow-hidden rounded-full border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/20">
                  <User className="h-6 w-6 text-[var(--panel-primary)]" />
                </div>
                <div className="flex flex-col">
                  <div className={cn("text-base font-bold leading-tight", softDarkMode ? "text-slate-900" : "text-slate-100")}>{displayName}</div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[var(--panel-primary)]">Üye</div>
                </div>
              </div>

              <nav className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/panel"}
                    className={({ isActive }) => {
                      const forcedInactive = item.to === "/panel/cember" && location.pathname.startsWith("/panel/cember/gorevler");
                      const active = isActive && !forcedInactive;
                      return cn(
                        "flex items-center gap-3 rounded-xl py-3 text-sm font-medium transition-all",
                        item.indent ? "pl-10 pr-4" : "px-4",
                        active
                          ? softDarkMode
                            ? "bg-gradient-to-r from-[#b86bff] to-[#a855f7] text-[#241530] shadow-[0_10px_22px_rgba(168,85,247,0.32)]"
                            : "bg-[var(--panel-primary)] text-white shadow-lg shadow-[color:rgba(55,19,236,0.20)]"
                          : cn(
                              "hover:bg-[var(--panel-primary)]/10 hover:text-[var(--panel-primary)]",
                              softDarkMode ? "text-slate-700" : "text-slate-400",
                            ),
                      );
                    }}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                    {item.to === "/panel/kesfet/sohbet" && sohbetUnreadTotal > 0 ? (
                  <span className="ml-auto grid min-h-5 min-w-5 place-items-center rounded-full bg-[var(--panel-primary)] px-1 text-[10px] font-bold text-white">
                    {Math.min(sohbetUnreadTotal, 99)}
                  </span>
                ) : null}
                {item.to === "/panel/bildirimler" && notificationUnreadTotal > 0 ? (
                  <span className="ml-auto grid min-h-5 min-w-5 place-items-center rounded-full bg-[var(--panel-primary)] px-1 text-[10px] font-bold text-white">
                    {Math.min(notificationUnreadTotal, 99)}
                  </span>
                ) : null}
                  </NavLink>
                ))}
              </nav>

              {sidebarExtra ? <div className="pt-2">{sidebarExtra}</div> : null}
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Link
                  to="/panel/magaza"
                  className={cn(
                    "grid place-items-center rounded-xl px-3 py-2 text-xs font-semibold transition-all",
                    softDarkMode
                      ? "border border-slate-300/70 text-slate-700"
                      : "border border-[var(--panel-primary)]/12 text-slate-200",
                  )}
                >
                  Mağaza
                </Link>
                <Link
                  to="/panel/siparislerim"
                  className={cn(
                    "grid place-items-center rounded-xl px-3 py-2 text-xs font-semibold transition-all",
                    softDarkMode
                      ? "border border-slate-300/70 text-slate-700"
                      : "border border-[var(--panel-primary)]/12 text-slate-200",
                  )}
                >
                  Siparişler
                </Link>
                <Link
                  to="/panel/ayarlar"
                  className={cn(
                    "grid place-items-center rounded-xl px-3 py-2 text-xs font-semibold transition-all",
                    softDarkMode
                      ? "border border-slate-300/70 text-slate-700"
                      : "border border-[var(--panel-primary)]/12 text-slate-200",
                  )}
                >
                  Ayarlar
                </Link>
              </div>

              <button
                type="button"
                onClick={onSignOut}
                disabled={signingOut}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300/70 px-3 py-2 text-xs font-semibold text-slate-700 transition-all disabled:opacity-60"
              >
                <LogOut className="h-4 w-4" />
                Çıkış
              </button>

              <Link
                to="/panel/destek"
                className={cn(
                  "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all hover:bg-[var(--panel-primary)]/5",
                  softDarkMode
                    ? "border border-slate-300/70 text-slate-600"
                    : "border border-[var(--panel-primary)]/12 text-slate-400",
                )}
              >
                <Settings className="h-5 w-5" />
                Destek Merkezi
              </Link>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <aside className="hidden">
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="flex flex-col gap-8">
            <div className="flex items-center gap-3 px-2">
            <div className="grid size-12 place-items-center overflow-hidden rounded-full border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/20">
              <User className="h-6 w-6 text-[var(--panel-primary)]" />
            </div>
            <div className="flex flex-col">
              <div className="text-base font-bold leading-tight text-slate-900 dark:text-slate-100">{displayName}</div>
              <div className="text-xs font-semibold uppercase tracking-wider text-[var(--panel-primary)]">Üye</div>
            </div>
          </div>

          <nav className="flex flex-col gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/panel"}
                className={({ isActive }) => {
                  const forcedInactive = item.to === "/panel/cember" && location.pathname.startsWith("/panel/cember/gorevler");
                  const active = isActive && !forcedInactive;
                  return cn(
                    "flex items-center gap-3 rounded-xl py-3 text-sm font-medium transition-all",
                    item.indent ? "pl-10 pr-4" : "px-4",
                    active
                      ? "bg-[var(--panel-primary)] text-white shadow-lg shadow-[color:rgba(55,19,236,0.20)]"
                      : "text-slate-600 hover:bg-[var(--panel-primary)]/10 hover:text-[var(--panel-primary)] dark:text-slate-400",
                  );
                }}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
                {item.to === "/panel/kesfet/sohbet" && sohbetUnreadTotal > 0 ? (
                  <span className="ml-auto grid min-h-5 min-w-5 place-items-center rounded-full bg-[var(--panel-primary)] px-1 text-[10px] font-bold text-white">
                    {Math.min(sohbetUnreadTotal, 99)}
                  </span>
                ) : null}
                {item.to === "/panel/bildirimler" && notificationUnreadTotal > 0 ? (
                  <span className="ml-auto grid min-h-5 min-w-5 place-items-center rounded-full bg-[var(--panel-primary)] px-1 text-[10px] font-bold text-white">
                    {Math.min(notificationUnreadTotal, 99)}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </nav>

          {sidebarExtra ? <div className="pt-2 px-2">{sidebarExtra}</div> : null}
          </div>
        </div>

        <div className="px-2">
          <Link
            to="/panel/destek"
            className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-500 transition-all hover:bg-[var(--panel-primary)]/5 dark:border-[var(--panel-primary)]/5 dark:text-slate-500"
          >
            <Settings className="h-5 w-5" />
            Destek Merkezi
          </Link>
        </div>
      </aside>

      <main
        className={cn(
          "flex h-dvh min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-[#f6f6f8] dark:bg-[#131022]",
          isSohbetDetailRoute ? "overflow-y-hidden pb-0 touch-pan-y" : "overflow-y-auto pb-24",
        )}
      >
        {isSohbetDetailRoute ? null : (
          <header className="sticky top-0 z-50 flex items-center justify-between bg-[#f6f6f8]/90 px-4 py-4 shadow-[0_10px_35px_-24px_rgba(15,23,42,0.85)] backdrop-blur-xl dark:bg-[#131022]/90 dark:shadow-[0_14px_40px_-28px_rgba(0,0,0,0.95)] sm:px-6 sm:py-5">
          <div className="flex items-center gap-3">
            <div className="grid size-9 place-items-center rounded-2xl bg-[var(--panel-primary)] text-white shadow-lg shadow-[color:rgba(55,19,236,0.30)]">
              <panelHeader.icon className="h-4 w-4" />
            </div>
            <div>
              <div className="text-base font-extrabold tracking-tight sm:text-xl">{panelHeader.title}</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 lg:hidden">Mobil panel görünümü</div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div ref={searchContainerRef} className="relative hidden">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => {
                  if (!searchOpen) setSearchOpen(true);

                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    if (!filteredSearchSuggestions.length) return;
                    setActiveSearchIndex((prev) => (prev + 1) % filteredSearchSuggestions.length);
                    return;
                  }

                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    if (!filteredSearchSuggestions.length) return;
                    setActiveSearchIndex((prev) => (prev <= 0 ? filteredSearchSuggestions.length - 1 : prev - 1));
                    return;
                  }

                  if (e.key === "Enter") {
                    e.preventDefault();
                    const target = filteredSearchSuggestions[activeSearchIndex] ?? filteredSearchSuggestions[0];
                    if (target) openSuggestion(target);
                    return;
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    setSearchOpen(false);
                    setActiveSearchIndex(0);
                  }
                }}
                className="w-72 rounded-xl bg-slate-100 py-2 pl-9 pr-4 text-sm outline-none focus:ring-2 focus:ring-[var(--panel-primary)] dark:bg-[var(--panel-primary)]/10"
                placeholder="Panel içinde ara..."
              />

              {searchOpen ? (
                <div className="absolute right-0 top-[calc(100%+10px)] z-30 w-[28rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-[var(--panel-primary)]/15 dark:bg-slate-900">
                  {searchLoading ? <div className="px-4 py-3 text-xs text-slate-500">Arama verileri yükleniyor...</div> : null}

                  {filteredSearchSuggestions.length ? (
                    <div className="max-h-96 overflow-y-auto p-2">
                      {filteredSearchSuggestions.map((item, idx) => (
                        <button
                          key={item.id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => openSuggestion(item)}
                          className={cn(
                            "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition",
                            idx === activeSearchIndex
                              ? "bg-[var(--panel-primary)]/10"
                              : "hover:bg-slate-100 dark:hover:bg-[var(--panel-primary)]/10",
                          )}
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{item.title}</div>
                            <div className="truncate text-xs text-slate-500 dark:text-slate-400">{item.subtitle}</div>
                          </div>
                          <span className="ml-3 shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-[var(--panel-primary)]/15 dark:text-slate-300">
                            {item.group}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : !searchLoading ? (
                    <div className="px-4 py-3 text-xs text-slate-500">Sonuç bulunamadı.</div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className={cn(
                "inline-flex items-center rounded-xl border px-3 py-2 text-sm font-semibold transition-colors",
                softDarkMode
                  ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  : "border-white/20 bg-white/10 text-white hover:bg-white/20",
              )}
              aria-label="Menüyü aç"
            >
              <Menu className="h-4 w-4" />
            </button>

            <Link
              to="/panel/bildirimler"
              className={cn(
                "relative inline-flex items-center rounded-xl border px-3 py-2 text-sm font-semibold transition-colors",
                softDarkMode
                  ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  : "border-white/20 bg-white/10 text-white hover:bg-white/20",
              )}
              aria-label="Bildirimler"
            >
              <Bell className="h-4 w-4" />
              {notificationUnreadTotal > 0 ? (
                <span className="absolute -right-2 -top-2 grid min-h-5 min-w-5 place-items-center rounded-full bg-[var(--panel-primary)] px-1 text-[10px] font-bold text-white">
                  {Math.min(notificationUnreadTotal, 99)}
                </span>
              ) : null}
            </Link>

            <Link
              to="/panel/magaza"
              className={cn(
                "hidden items-center rounded-xl border px-3 py-2 text-sm font-semibold transition-colors",
                softDarkMode
                  ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  : "border-white/20 bg-white/10 text-white hover:bg-white/20",
              )}
              aria-label="Mağaza"
            >
              <ShoppingBag className="h-4 w-4" />
            </Link>

            <Link
              to="/panel/siparislerim"
              className={cn(
                "hidden items-center rounded-xl border px-3 py-2 text-sm font-semibold transition-colors",
                softDarkMode
                  ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  : "border-white/20 bg-white/10 text-white hover:bg-white/20",
              )}
              aria-label="Siparişlerim"
            >
              <ReceiptText className="h-4 w-4" />
            </Link>

            <Link
              to="/panel/ayarlar"
              className={cn(
                "hidden items-center rounded-xl border px-3 py-2 text-sm font-semibold transition-colors",
                softDarkMode
                  ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  : "border-white/20 bg-white/10 text-white hover:bg-white/20",
              )}
              aria-label="Ayarlar"
            >
              <Settings className="h-4 w-4" />
            </Link>

            <button
              type="button"
              onClick={onSignOut}
              disabled={signingOut}
              className={cn(
                "hidden items-center rounded-xl border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
                softDarkMode
                  ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  : "border-white/20 bg-white/10 text-white hover:bg-white/20",
              )}
              aria-label="Çıkış yap"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          </header>
        )}

        <div
          className={cn(
            "min-w-0",
            isSohbetDetailRoute ? "flex min-h-0 flex-1 overflow-hidden px-0 py-0" : "px-4 py-4 sm:p-6",
          )}
        >
          {shouldLockCurrentRoute && activeRouteFeatureGate ? (
            <div className="mx-auto w-full max-w-[760px]">
              {activeRouteFeatureGate.featureKey === "nearby_rituals" ? (
                <div className="overflow-hidden rounded-[2.25rem] border border-[#e7dcec] bg-[#fbf4ff] shadow-[0_20px_40px_rgba(112,42,225,0.14)]">
                  <div className="relative h-[300px] overflow-hidden bg-gradient-to-br from-[#5f4c82] via-[#74609a] to-[#8f76b6]">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.22),transparent_45%),radial-gradient(circle_at_85%_70%,rgba(255,255,255,0.12),transparent_40%)]" />
                    <div className="absolute left-[34%] top-[32%] grid h-12 w-12 place-items-center rounded-full border-2 border-white/70 bg-[var(--panel-primary)] text-white shadow-lg">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <div className="absolute right-[22%] top-[58%] grid h-12 w-12 place-items-center rounded-full border-2 border-white/70 bg-[var(--panel-primary)] text-white shadow-lg">
                      <MapPin className="h-5 w-5" />
                    </div>

                    <div className="absolute inset-x-0 bottom-5 flex gap-2 overflow-x-auto px-5 pb-1">
                      <span className="shrink-0 rounded-full bg-[var(--panel-primary)] px-4 py-2 text-xs font-bold text-white">Tümü</span>
                      <span className="shrink-0 rounded-full bg-white/90 px-4 py-2 text-xs font-semibold text-slate-700">Yakın (2km)</span>
                      <span className="shrink-0 rounded-full bg-white/90 px-4 py-2 text-xs font-semibold text-slate-700">Meditasyon</span>
                      <span className="shrink-0 rounded-full bg-white/90 px-4 py-2 text-xs font-semibold text-slate-700">Şimdi</span>
                    </div>
                  </div>

                  <div className="relative -mt-6 rounded-t-[2rem] bg-[#fbf4ff] px-5 pb-6 pt-6">
                    <div className="mb-4 flex items-start justify-between">
                      <div>
                        <div className="text-3xl font-black tracking-tight text-[#12172b]">Yakındaki Ritüeller</div>
                        <p className="mt-1 text-sm text-slate-500">Ruhun için doğru anı keşfet</p>
                      </div>
                      <div className="inline-flex items-center gap-1 rounded-full bg-[#f1e6f5] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#7a4a97]">
                        <Sparkles className="h-3.5 w-3.5" /> Pro
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="rounded-2xl border border-[#ece1f0] bg-white px-4 py-4 shadow-[0_10px_30px_rgba(112,42,225,0.06)]">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#f1e6f5] text-[var(--panel-primary)]">
                              <Sparkles className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-lg font-bold text-[#12172b]">Dolunay Arınması</div>
                              <div className="mt-0.5 text-xs text-slate-500">Caddebostan Sahili • 1.2 km</div>
                            </div>
                          </div>
                          <div className="rounded-xl bg-[#f3ecf8] px-3 py-1 text-sm font-bold text-[var(--panel-primary)]">20:30</div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-[#ece1f0] bg-white/80 px-4 py-4 opacity-70 blur-[0.5px]">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#f1e6f5] text-[var(--panel-primary)]">
                              <MapPin className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-lg font-bold text-[#12172b]">Sabah Topraklaması</div>
                              <div className="mt-0.5 text-xs text-slate-500">Göztepe Parkı • 2.8 km</div>
                            </div>
                          </div>
                          <div className="rounded-xl bg-[#f3ecf8] px-3 py-1 text-sm font-bold text-[var(--panel-primary)]">07:15</div>
                        </div>
                      </div>
                    </div>

                    <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[170px] bg-gradient-to-t from-[#fbf4ff] via-[#fbf4ff]/95 to-transparent" />

                    <div className="relative z-10 mt-8 px-2 text-center">
                      <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-[var(--panel-primary)] to-[#9f72ff] text-white shadow-xl">
                        <Sparkles className="h-7 w-7" />
                      </div>
                      <h3 className="text-3xl font-black tracking-tight text-[#12172b]">Yakınındaki Enerjileri Gör</h3>
                      <p className="mx-auto mt-2 max-w-[330px] text-base text-slate-600">
                        Pro üyeliğe geçerek çevrendeki ritüellere katıl ve topluluğun bir parçası ol.
                      </p>
                      <Link
                        to={`/panel/magaza?from=${encodeURIComponent(activeRouteFeatureGate.ctaFrom)}`}
                        className="mt-5 block w-full rounded-full bg-gradient-to-r from-[var(--panel-primary)] to-[#9f72ff] py-4 text-center text-2xl font-black tracking-tight text-white shadow-[0_20px_40px_rgba(112,42,225,0.22)]"
                      >
                        Hemen Yükselt
                      </Link>
                      <Link
                        to={`/panel/magaza?from=${encodeURIComponent(activeRouteFeatureGate.ctaFrom)}`}
                        className="mt-3 inline-block text-sm font-bold text-[var(--panel-primary)]"
                      >
                        Ücretsiz Denemeyi Başlat
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-[2.25rem] bg-gradient-to-br from-[var(--panel-primary)] to-[#6b2fd6] p-6 text-white shadow-[0_20px_40px_rgba(55,19,236,0.22)]">
                  <div className="text-[44px] font-black leading-none tracking-tight">🔒</div>
                  <div className="mt-3 text-4xl font-black tracking-tight">Premium'a Geç</div>
                  <p className="mt-3 max-w-[620px] text-lg text-white/85">{activeRouteFeatureGate.description}</p>

                  <div className="mt-6 space-y-3">
                    {MEMBERSHIP_PLANS.filter((plan) => plan.code !== "free").map((plan) => (
                      <div key={plan.code} className="rounded-[1.5rem] border border-white/25 bg-white/90 px-6 py-4 text-slate-900 shadow-sm">
                        <div className="flex items-end justify-between gap-3">
                          <div>
                            <div className="text-[40px] font-black leading-none tracking-tight">{plan.name}</div>
                            <div className="mt-1 text-[28px] font-semibold text-slate-700">
                              {typeof plan.pricesTry.annual === "number" ? `Yıllık ${plan.pricesTry.annual} ₺` : ""}
                            </div>
                          </div>
                          <div className="text-[44px] font-black leading-none tracking-tight text-slate-900">
                            {typeof plan.pricesTry.monthly === "number" ? `${plan.pricesTry.monthly} ₺/ay` : "-"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={`/panel/magaza?from=${encodeURIComponent(activeRouteFeatureGate.ctaFrom)}`}
                    className="mt-6 block w-full rounded-[1.5rem] bg-white py-4 text-center text-4xl font-black tracking-tight text-[var(--panel-primary)] transition hover:brightness-95"
                  >
                    Planları İncele
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <Outlet />
          )}
        </div>

        {isSohbetDetailRoute ? null : (
          <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--panel-primary)]/10 bg-white/95 px-2 pb-[calc(env(safe-area-inset-bottom)+0.35rem)] pt-2 shadow-[0_-18px_40px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-white/10 dark:bg-[#130d24]/92">
          <div className="grid grid-cols-5 gap-1">
            {mobilePrimaryNavItems.map((item) => {
              const active = isMobileNavItemActive(location.pathname, item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "relative flex min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2.5 text-[11px] font-semibold transition-all",
                    active
                      ? "bg-[var(--panel-primary)] text-white shadow-lg shadow-[color:rgba(55,19,236,0.20)]"
                      : "text-slate-500 hover:bg-[var(--panel-primary)]/10 hover:text-[var(--panel-primary)] dark:text-slate-400",
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span className="truncate">{item.label}</span>
                  {item.to === "/panel/kesfet/sohbet" && sohbetUnreadTotal > 0 ? (
                    <span className="absolute right-3 top-2 grid min-h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                      {Math.min(sohbetUnreadTotal, 99)}
                    </span>
                  ) : null}
                  {item.to === "/panel/bildirimler" && notificationUnreadTotal > 0 ? (
                    <span className="absolute right-3 top-2 grid min-h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                      {Math.min(notificationUnreadTotal, 99)}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
          </nav>
        )}
      </main>
      </div>
    </PanelSidebarExtraContext.Provider>
  );
}
